import { useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { 
  XMarkIcon, 
  GlobeAltIcon,
  ComputerDesktopIcon,
  ClockIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  EyeIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline'
import { authFetch } from '@/utils/api'

interface UserAttributionModalProps {
  isOpen: boolean
  onClose: () => void
  sessionId: string
}

interface AttributionData {
  session_id: string
  ip_address: string
  first_seen: string
  last_seen: string
  total_requests: number
  unique_pages: number
  fingerprint: any
  network: any
  geolocation: any
  behavior: any
  technical: any
  bot_analysis: any
  exposure: ExposureItem[]
  request_history: any[]
}

interface ExposureItem {
  id: string
  layer: string
  value: string
  reveals: string
  opsec: string
  severity: 'high' | 'medium' | 'low' | 'info'
}

const UserAttributionModal = ({ isOpen, onClose, sessionId }: UserAttributionModalProps) => {
  const [attribution, setAttribution] = useState<AttributionData | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen && sessionId) {
      fetchAttributionData()
    }
  }, [isOpen, sessionId])

  const fetchAttributionData = async () => {
    setLoading(true)
    try {
      const response = await authFetch(`/api/monitoring/user-attribution/${sessionId}`)
      const result = await response.json()
      if (result.success) {
        setAttribution(result.data.attribution)
      }
    } catch (error) {
      console.error('Error fetching attribution data:', error)
    }
    setLoading(false)
  }

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'high': return 'border-red-200 bg-red-50'
      case 'medium': return 'border-orange-200 bg-orange-50'
      case 'low': return 'border-yellow-200 bg-yellow-50'
      default: return 'border-gray-200 bg-gray-50'
    }
  }

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-orange-100 text-orange-800'
      case 'low': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'low': return 'text-green-600 bg-green-100'
      case 'medium': return 'text-yellow-600 bg-yellow-100'
      case 'high': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`
    if (minutes > 0) return `${minutes}m ${secs}s`
    return `${secs}s`
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <ComputerDesktopIcon className="h-6 w-6 mr-2 text-blue-600" />
                    User Attribution Analysis - Session {sessionId.slice(-8)}
                  </div>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </Dialog.Title>

                {loading ? (
                  <div className="mt-4 text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading user attribution data...</p>
                  </div>
                ) : attribution ? (
                  <div className="mt-6 space-y-6">
                    {/* Session Overview */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Session Overview</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Total Requests</p>
                          <p className="text-2xl font-bold text-gray-900">{attribution.total_requests}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Unique Pages</p>
                          <p className="text-2xl font-bold text-gray-900">{attribution.unique_pages}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Session Duration</p>
                          <p className="text-2xl font-bold text-gray-900">{formatDuration(attribution.behavior?.session_duration || 0)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Bot Risk</p>
                          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getRiskColor(attribution.bot_analysis?.risk_level || 'unknown')}`}>
                            {attribution.bot_analysis?.risk_level || 'Unknown'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Network & Location */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                          <GlobeAltIcon className="h-5 w-5 mr-2 text-blue-600" />
                          Network & Location
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm font-medium text-gray-500">IP Address</p>
                            <p className="text-sm text-gray-900">{attribution.network?.ip_address || 'Unknown'}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-500">ISP</p>
                              <p className="text-sm text-gray-900">{attribution.network?.isp || 'Unknown'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">ASN</p>
                              <p className="text-sm text-gray-900">
                                {attribution.network?.asn || 'Unknown'}
                                {attribution.network?.as_name ? ` (${attribution.network.as_name})` : ''}
                              </p>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Organization</p>
                            <p className="text-sm text-gray-900">{attribution.network?.organization || 'Unknown'}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-500">Connection Type</p>
                              <p className="text-sm text-gray-900">{attribution.network?.connection_type || 'Unknown'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Reverse DNS</p>
                              <p className="text-sm text-gray-900 break-all">{attribution.network?.reverse_dns || 'None'}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Location</p>
                            <p className="text-sm text-gray-900">
                              {[attribution.geolocation?.city, attribution.geolocation?.region, attribution.geolocation?.country]
                                .filter(Boolean).join(', ') || 'Unknown'}
                              {attribution.geolocation?.postal_code ? ` ${attribution.geolocation.postal_code}` : ''}
                            </p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-500">Coordinates</p>
                              <p className="text-sm text-gray-900">
                                {attribution.geolocation?.latitude != null && attribution.geolocation?.longitude != null
                                  ? `${attribution.geolocation.latitude}, ${attribution.geolocation.longitude}`
                                  : 'Unknown'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">IP Timezone</p>
                              <p className="text-sm text-gray-900">{attribution.geolocation?.timezone || 'Unknown'}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 pt-1">
                            {attribution.network?.proxy_detected && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                <ExclamationTriangleIcon className="h-3 w-3 mr-1" /> Proxy / VPN
                              </span>
                            )}
                            {attribution.network?.hosting && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                Datacenter / Hosting
                              </span>
                            )}
                            {attribution.network?.mobile && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Mobile carrier
                              </span>
                            )}
                            {attribution.network?.data_source && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                source: {attribution.network.data_source}
                              </span>
                            )}
                          </div>
                          {attribution.network?.note && (
                            <p className="text-xs text-gray-500 italic">{attribution.network.note}</p>
                          )}
                        </div>
                      </div>

                      {/* Browser Fingerprint */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                          <ComputerDesktopIcon className="h-5 w-5 mr-2 text-purple-600" />
                          Browser Fingerprint
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm font-medium text-gray-500">User Agent</p>
                            <p className="text-xs text-gray-900 break-all">{attribution.fingerprint?.user_agent}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-500">Screen</p>
                              <p className="text-sm text-gray-900">{attribution.fingerprint?.screen_resolution}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Viewport</p>
                              <p className="text-sm text-gray-900">{attribution.fingerprint?.viewport}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-500">Language</p>
                              <p className="text-sm text-gray-900">{attribution.fingerprint?.language}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Timezone</p>
                              <p className="text-sm text-gray-900">{attribution.fingerprint?.timezone}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Canvas Fingerprint</p>
                            <p className="text-xs text-gray-900 font-mono">{attribution.fingerprint?.canvas_fingerprint}</p>
                          </div>
                        </div>
                      </div>

                      {/* Behavioral Analysis */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                          <ClockIcon className="h-5 w-5 mr-2 text-green-600" />
                          Behavioral Analysis
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm font-medium text-gray-500">Request Frequency</p>
                            <p className="text-sm text-gray-900">{attribution.behavior?.request_frequency}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Navigation Pattern</p>
                            <p className="text-sm text-gray-900">{attribution.behavior?.navigation_pattern}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-500">Avg. Time on Page</p>
                              <p className="text-sm text-gray-900">{attribution.behavior?.time_on_page_avg}s</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Interactions</p>
                              <p className="text-sm text-gray-900">{attribution.behavior?.interaction_events}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Mouse & Scroll Behavior</p>
                            <p className="text-sm text-gray-900">
                              {attribution.behavior?.mouse_movements}, {attribution.behavior?.scroll_behavior}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Bot Analysis */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                          <ShieldCheckIcon className="h-5 w-5 mr-2 text-red-600" />
                          Bot Detection Analysis
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-500">Risk Score</span>
                            <span className="text-2xl font-bold text-gray-900">{attribution.bot_analysis?.risk_score}/100</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-500">Human Probability</span>
                            <span className="text-lg font-semibold text-green-600">
                              {Math.round((attribution.bot_analysis?.human_probability || 0) * 100)}%
                            </span>
                          </div>
                          
                          {attribution.bot_analysis?.indicators?.positive?.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-500 mb-2">Positive Indicators</p>
                              <div className="space-y-1">
                                {attribution.bot_analysis.indicators.positive.map((indicator: string, index: number) => (
                                  <div key={index} className="flex items-center text-sm text-green-600">
                                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                                    {indicator}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {attribution.bot_analysis?.indicators?.negative?.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-500 mb-2">Negative Indicators</p>
                              <div className="space-y-1">
                                {attribution.bot_analysis.indicators.negative.map((indicator: string, index: number) => (
                                  <div key={index} className="flex items-center text-sm text-red-600">
                                    <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                                    {indicator}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Attribution Exposure Breakdown */}
                    {attribution.exposure && attribution.exposure.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                          <EyeIcon className="h-5 w-5 mr-2 text-red-600" />
                          Attribution Exposure Breakdown
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Every layer below was derived from this single visit. This is what general web
                          browsing reveals about an operation - and how to reduce that exposure.
                        </p>
                        <div className="space-y-3">
                          {attribution.exposure.map((item) => (
                            <div key={item.id} className={`border rounded-lg p-3 ${getSeverityStyles(item.severity)}`}>
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-semibold text-gray-900">{item.layer}</p>
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium uppercase ${getSeverityBadge(item.severity)}`}>
                                  {item.severity}
                                </span>
                              </div>
                              <p className="text-sm text-gray-900 font-mono break-all mb-2">{item.value}</p>
                              <div className="flex items-start text-xs text-gray-700 mb-1">
                                <ExclamationTriangleIcon className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0 text-gray-500" />
                                <span><span className="font-medium">Reveals:</span> {item.reveals}</span>
                              </div>
                              <div className="flex items-start text-xs text-green-700">
                                <LockClosedIcon className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                                <span><span className="font-medium">OPSEC:</span> {item.opsec}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Request History */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Request History</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Time
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Target/URL
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Response Time
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {attribution.request_history?.map((request: any, index: number) => (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {new Date(request.timestamp).toLocaleTimeString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {request.method || request.type || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {typeof request.url === 'string' ? request.url : 
                                   typeof request.url === 'object' && request.url ? 
                                   `${request.url.tagName || 'Element'}: ${request.url.textContent?.substring(0, 50) || 'N/A'}` : 
                                   'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    (request.status === 200 || !request.status) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {request.status || 'OK'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {request.response_time ? `${request.response_time}ms` : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Educational Note */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                        <div>
                          <h5 className="text-sm font-medium text-blue-900">Educational Purpose</h5>
                          <p className="text-sm text-blue-700 mt-1">
                            This detailed attribution analysis demonstrates the extensive digital footprint that users leave 
                            when browsing websites. It shows how websites can track, profile, and analyze visitor behavior 
                            for security, analytics, and personalization purposes. This information highlights the importance 
                            of privacy-conscious browsing and understanding your digital footprint.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 text-center py-12">
                    <p className="text-gray-600">No attribution data available for this session.</p>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default UserAttributionModal

